import { ScheduleType } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-type.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { getLogger } from '@/logger/logger';
import { authStore } from '@/store/auth.store';
import { openDifferentCityConfirmation } from '@/store/cart/different-city-confirmation.store';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { AnalyticsScheduleType } from '@/types/enums/analytics/search/search-analytics.enum';
import { isDifferentCity } from '@/utils/formatters/city/is-different-city.util';

const logger = getLogger('cart.different-city');

export type DifferentCityScheduleType = ScheduleType | AnalyticsScheduleType;

export interface DifferentCityFacility {
  id: string;
  city?: string;
  state?: string;
}

export interface DifferentCityTarget {
  partnerId: string;
  facility: DifferentCityFacility;
  isOnline: boolean;
}

export async function confirmDifferentCity(
  target: DifferentCityTarget,
  scheduleType: DifferentCityScheduleType,
): Promise<boolean> {
  if (target.isOnline) {
    return true;
  }

  const { authenticatedUser, isBasicProfile } = authStore.getState();

  if (isBasicProfile) {
    logger.info('Skipped the different-city warning', {
      cart: { event: 'different_city_skipped', reason: 'basic_profile', partner_id: target.partnerId },
    });

    return true;
  }

  const registeredAddress = authenticatedUser?.address;

  if (!registeredAddress?.city || !registeredAddress?.state) {
    logger.info('Skipped the different-city warning', {
      cart: { event: 'different_city_skipped', reason: 'no_registered_city', partner_id: target.partnerId },
    });

    return true;
  }

  const { facility } = target;

  if (!facility.city || !facility.state) {
    logger.warn('Skipped the different-city warning', {
      cart: {
        event: 'different_city_skipped',
        reason: 'facility_without_city',
        partner_id: target.partnerId,
        partner_facility_id: facility.id,
      },
    });

    return true;
  }

  if (!isDifferentCity(registeredAddress, facility)) {
    return true;
  }

  const answer = await openDifferentCityConfirmation();

  if (answer === 'dropped') {
    return false;
  }

  getAnalyticsPlugin().track(
    answer === 'accepted'
      ? AnalyticsEnum.ACCEPTED_DIFFERENT_CITY_WARNING
      : AnalyticsEnum.DISMISSED_DIFFERENT_CITY_WARNING,
    { partner_id: target.partnerId, schedule_type: scheduleType },
  );

  return answer === 'accepted';
}
